// A 12-point pitch-class clock (SVG): the chromatic circle with a chord/scale
// drawn as a polygon. The Lab's lingua franca — symmetry reads as literal
// geometric symmetry (a dim7 is a square, an augmented triad a triangle).

import { useMemo } from 'react';
import { spellNote, type Accidental, type PitchClass } from '@/music/notes';

interface PitchClassClockProps {
  /** Member pitch classes (the polygon's vertices). */
  pcs: PitchClass[];
  size?: number;
  accidental?: Accidental;
  /** Ring these pitch classes (e.g. the roots of neighbour chords). */
  highlight?: PitchClass[];
  title?: string;
  onTap?: () => void;
}

export function PitchClassClock({
  pcs,
  size = 168,
  accidental = 'flat',
  highlight = [],
  title,
  onTap,
}: PitchClassClockProps) {
  const c = size / 2;
  const rDots = c - 22;
  const member = useMemo(() => new Set(pcs.map((p) => ((p % 12) + 12) % 12)), [pcs]);
  const ring = useMemo(() => new Set(highlight.map((p) => ((p % 12) + 12) % 12)), [highlight]);
  const angle = (pc: number) => ((pc / 12) * 2 - 0.5) * Math.PI; // 12 o'clock = C
  const x = (pc: number, r: number) => c + r * Math.cos(angle(pc));
  const y = (pc: number, r: number) => c + r * Math.sin(angle(pc));
  const poly = [...member].sort((a, b) => a - b);

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      role={onTap ? 'button' : 'img'}
      aria-label={title ?? `Pitch classes ${poly.map((p) => spellNote(p, accidental)).join(' ')}`}
      onClick={onTap}
      style={onTap ? { cursor: 'pointer' } : undefined}
      className="pc-clock"
    >
      <circle cx={c} cy={c} r={rDots} fill="none" stroke="var(--hairline)" />
      {poly.length > 1 && (
        <polygon
          points={poly.map((p) => `${x(p, rDots)},${y(p, rDots)}`).join(' ')}
          fill="var(--accent)"
          fillOpacity={0.14}
          stroke="var(--accent)"
          strokeWidth={1.8}
          strokeLinejoin="round"
        />
      )}
      {Array.from({ length: 12 }, (_, pc) => (
        <g key={pc}>
          {ring.has(pc) && (
            <circle
              cx={x(pc, rDots)}
              cy={y(pc, rDots)}
              r={9.5}
              fill="none"
              stroke="var(--danger)"
              strokeWidth={1.6}
            />
          )}
          <circle
            cx={x(pc, rDots)}
            cy={y(pc, rDots)}
            r={member.has(pc) ? 5 : 2.4}
            fill={member.has(pc) ? 'var(--accent)' : 'var(--faint)'}
          />
          <text
            x={x(pc, rDots + 14)}
            y={y(pc, rDots + 14)}
            textAnchor="middle"
            dominantBaseline="central"
            fontSize={10}
            fill={member.has(pc) ? 'var(--ink)' : 'var(--muted)'}
            fontWeight={member.has(pc) ? 700 : 400}
          >
            {spellNote(pc, accidental)}
          </text>
        </g>
      ))}
      {title && (
        <text
          x={c}
          y={c}
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={12}
          fontWeight={700}
          fill="var(--ink)"
        >
          {title}
        </text>
      )}
    </svg>
  );
}
